import { useController } from 'react-hook-form';
import { FormControl } from '../EntryForm';
import { RadioGroup } from '../component/RadioGroup';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { OptionalDescriptionTooltip } from '../component/OptionalDescriptionTooltip';
import { CategoryFixedFormItem } from '../data/useProjectData/useProjectData';
import { useFilteringDependencies } from './useFilteringDependencies';
import { backendValueToFormValue } from '../utils/parse-backend-values/backendValueToFormValue';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import clsx from 'clsx';

interface Props {
	control: FormControl;
	item: CategoryFixedFormItem;
	disabled?: boolean;
}

export const CategoryFixedVariableInput = ({ item, control, disabled }: Props) => {
	const variable = item.variable;

	const {
		field: { value, onChange },
		fieldState
	} = useController({
		name: variable.variableName,
		control
	});

	const filteredCategories = useFilteringDependencies({
		variable,
		dependencies: item.dependencies,
		control
	});

	const options = useMemo(
		() =>
			variable.allowedCategories
				.filter(category => filteredCategories.has(category.value))
				.map(category => ({
					label: category.label,
					value: category.value
				})),
		[variable.allowedCategories, filteredCategories]
	);

	useEffect(() => {
		if (variable.entryType === 'calculated') {
			return;
		}
		const selectedValue = value;
		const allowedValues = new Set(options.map(option => option.value));
		const currentValueLegal = allowedValues.has(selectedValue);
		const emptyValue = backendValueToFormValue({ value: null, variable });
		if (!currentValueLegal && !isEqual(value, emptyValue)) {
			onChange(emptyValue);
		}
	}, [options, value]);

	if (item.layoutType === 'dropdown') {
		const selectedOption = options.find(option => option.value === value);

		return (
			<CreatableSelect
				className={clsx(item.hideFromUi && 'hidden')}
				label={item.variable.variableLabel}
				items={options}
				required={variable.obligatory}
				value={selectedOption || null}
				onValueSelected={value => onChange(value ? value : '')}
				allowCreate={false}
				error={fieldState.error?.message?.toString()}
				disabled={disabled || variable.entryType === 'calculated'}
				LabelSuffix={<OptionalDescriptionTooltip description={variable.description} />}
			/>
		);
	}

	return (
		<RadioGroup
			className={clsx(item.hideFromUi && 'hidden')}
			categories={options}
			description={variable.description}
			allowCreate={false}
			control={control}
			disabled={disabled || variable.entryType === 'calculated'}
			label={item.variable.variableLabel}
			variableName={variable.variableName}
			obligatory={variable.obligatory}
			orientation={item.layoutType}
		/>
	);
};
